/**
 * «Utvikling» on Oversikt (meeting notes 2026-10-31): three bar charts from GET /admin/dashboard `charts` (core
 * dashboard.charts) — gifts per month, members in and out, people coming per event. Each chart has one line above it
 * in plain words about this month or the latest event: no percentage, no target, no trend line (Dashboard.tsx's header
 * rule stands). A chart the server left out (no permission, capability off) is not drawn at all; a chart with nothing in
 * it says «Ingen tall ennå» (BarChart's own empty state), and the card stays.
 */
import React from 'react';
import { Card } from './ui';
import { BarChart } from './BarChart';
import { kr } from '../lib/format';

interface Month { month: string; label_no: string; hint_no: string }
export interface DashCharts {
  gifts?: { months: (Month & { amount: number; cash: number | null })[]; thisMonth: { amount: number; cash: number | null }; withCash: boolean };
  members?: { months: (Month & { joined: number; left: number })[]; unknownJoin: number };
  rsvp?: { id: string; label_no: string; hint_no: string; coming: number; registered: number | null }[];
}

/** «1 person» / «3 personer». */
const n = (k: number, one: string, many: string) => `${k.toLocaleString('nb-NO')} ${k === 1 ? one : many}`;
/** «oktober 2026» → «oktober»: the reading is about this month, which needs no year. */
const monthName = (m: Month | undefined) => (m ? m.hint_no.replace(/\s\d{4}$/, '') : 'denne måneden');

function Chart({ id, title, reading, legend, children }: { id: string; title: string; reading: string; legend?: string; children: React.ReactNode }) {
  return (
    <section className="ov-chart" aria-labelledby={id}>
      <h4 id={id}>{title}</h4>
      <p className="ov-chart-reading">{reading}</p>
      {children}
      {legend && <p className="note ov-chart-legend">{legend}</p>}
    </section>
  );
}

/** The card, or nothing when the server sent no chart this person may see. */
export function DashboardCharts({ charts }: { charts?: DashCharts | null }) {
  if (!charts) return null;
  const { gifts, members, rsvp } = charts;
  if (!gifts && !members && !rsvp) return null;
  const parts: React.ReactNode[] = [];

  if (gifts) {
    const last = gifts.months[gifts.months.length - 1];
    const cash = gifts.withCash && (gifts.thisMonth.cash || 0) > 0 ? ` og ${kr(gifts.thisMonth.cash)} i kontanter` : '';
    parts.push(
      <Chart key="gifts" id="ov-chart-gifts" title="Gaver per måned"
        reading={gifts.thisMonth.amount > 0 || (gifts.thisMonth.cash || 0) > 0
          ? `I ${monthName(last)} har det kommet inn ${kr(gifts.thisMonth.amount)} med Vipps${cash}.`
          : `Ingen gaver er mottatt i ${monthName(last)} ennå.`}
        legend={gifts.withCash ? 'Grønn: Vipps, mottatt. Grå: kontant, bekreftet.' : 'Vipps-gaver som er mottatt, etter dagen gaven ble gitt.'}>
        <BarChart unit="kr" ariaLabel="Gaver per måned, siste tolv måneder"
          series={gifts.months.map((m) => ({ label: m.label_no, value: m.amount, hint: m.hint_no }))}
          secondary={gifts.withCash ? gifts.months.map((m) => ({ label: m.label_no, value: m.cash || 0 })) : undefined}
          seriesLabels={gifts.withCash ? ['Vipps', 'Kontant'] : undefined} />
      </Chart>,
    );
  }

  if (members) {
    const last = members.months[members.months.length - 1];
    const inn = last ? last.joined : 0, ut = last ? last.left : 0;
    parts.push(
      <Chart key="members" id="ov-chart-members" title="Medlemmer inn og ut"
        reading={inn || ut
          ? `I ${monthName(last)}: ${inn === 1 ? '1 nytt medlem' : `${inn.toLocaleString('nb-NO')} nye medlemmer`}, ${n(ut, 'utmeldt', 'utmeldte')}.`
          : `Ingen har meldt seg inn eller ut i ${monthName(last)}.`}
        legend={'Grønn: meldt inn (første medlemskap). Grå: meldt ut.'
          + (members.unknownJoin ? ' Medlemmer uten kjent innmeldingsdato (ofte fra importen) står ikke i grafen.' : '')}>
        <BarChart unit="count" ariaLabel="Medlemmer inn og ut, siste tolv måneder"
          series={members.months.map((m) => ({ label: m.label_no, value: m.joined, hint: m.hint_no }))}
          secondary={members.months.map((m) => ({ label: m.label_no, value: m.left }))}
          seriesLabels={['Inn', 'Ut']} />
      </Chart>,
    );
  }

  if (rsvp) {
    const latest = rsvp[rsvp.length - 1];
    const lists = rsvp.some((e) => e.registered !== null && e.registered > 0);
    parts.push(
      <Chart key="rsvp" id="ov-chart-rsvp" title="Påmeldte per arrangement"
        reading={latest ? `Sist: ${latest.hint_no} — ${latest.coming.toLocaleString('nb-NO')} har sagt at de kommer${latest.registered ? `, ${n(latest.registered, 'plass', 'plasser')} på påmeldingslisten` : ''}.`
          : 'Ingen har meldt seg på et arrangement ennå.'}
        legend={lists ? 'Grønn: «Kommer» i appen. Grå: plasser på påmeldingslisten.' : 'De siste fem arrangementene der noen har sagt at de kommer.'}>
        <BarChart unit="count" ariaLabel="Påmeldte per arrangement, de siste fem"
          series={rsvp.map((e) => ({ label: e.label_no, value: e.coming, hint: e.hint_no }))}
          secondary={lists ? rsvp.map((e) => ({ label: e.label_no, value: e.registered || 0 })) : undefined}
          seriesLabels={lists ? ['Kommer', 'På listen'] : undefined} />
      </Chart>,
    );
  }

  return (
    <Card title="Utvikling" className="ov-charts">
      <div className="card-body">{parts}</div>
    </Card>
  );
}

export default DashboardCharts;
